import { toast } from 'react-hot-toast';
import logger from './logger';

/**
 * Executes user-defined Client Scripts (browser-side, admin-authored) for a
 * given form-lifecycle event against a mutable copy of the document.
 *
 * Each script body runs with a single `frm` argument exposing a small,
 * Frappe-like API:
 *   frm.doc                  - the (mutable) document values object
 *   frm.get_value(field)     - read a field
 *   frm.set_value(field, v)  - write a field
 *   frm.msgprint(text)       - show an info toast
 *   frm.add_error(text)      - register a validation error (blocks save)
 *
 * Returning `false` from a `validate` script also blocks the save.
 *
 * Scripts execute in the user's own browser session (same trust boundary as
 * the rest of the SPA); each runs inside try/catch so a faulty script can
 * never crash the form.
 *
 * @returns {{ doc: object, errors: string[], blocked: boolean }}
 */
export function runClientScripts(scripts, event, doc) {
  const working = { ...(doc || {}) };
  const errors = [];
  let returnedFalse = false;

  (scripts || [])
    .filter((s) => s.active && s.event === event && s.code)
    .forEach((s) => {
      const frm = {
        doc: working,
        get_value: (field) => working[field],
        set_value: (field, value) => { working[field] = value; },
        msgprint: (msg) => toast(String(msg)),
        add_error: (msg) => { errors.push(String(msg)); },
      };
      try {
        // eslint-disable-next-line no-new-func
        const fn = new Function('frm', s.code);
        const result = fn(frm);
        if (result === false) returnedFalse = true;
      } catch (err) {
        logger.error(`Client script "${s.name}" failed`, err);
        errors.push(`[${s.name}] ${err.message}`);
      }
    });

  return { doc: working, errors, blocked: returnedFalse || errors.length > 0 };
}

export default runClientScripts;
